// @flow
import React, { useRef, useState, useLayoutEffect } from 'react';
import type { Element } from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import ReactSlickSlider from 'react-slick';
import Slide from './Slide';
import type { Styles } from 'shared/types';
import Icon from '@ff/ui/Icon';
import { zIndices } from 'shared/widgets/ProductSlideShow/versions/0.0.1/constants';

declare var IS_NODE: boolean;

if (!IS_NODE) {
  // Include CSS with carousel. Dynamic import() will cause jumping on load in mobile app
  require('slick-carousel/slick/slick.css'); // eslint-disable-line global-require
  require('slick-carousel/slick/slick-theme.css'); // eslint-disable-line global-require
}

type Props = {
  children: Element<typeof Slide>[],
  carouselAutoplaySpeed: string,
}

const defaultSliderSettings = {
  infinite: true,
  speed: 500,
  easing: 'ease-in-out',
  slidesToShow: 1,
  slidesToScroll: 1,
  initialSlide: 0,
  draggable: false,
  pauseOnHover: false,
  adaptiveHeight: false,
  arrows: false,
};

export const SLIDE_MIN_HEIGHT = 600;

export default function Slider(props: Props) {
  const { children, controlsEnabled, carouselAutoplaySpeed } = props;
  const sliderRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const [indicatorPosition, setIndicatorPosition] = useState(window?.innerHeight);

  // Detect bottom position for mobile browsers; handle resize
  useLayoutEffect(() => {
    const onResize = () => {
      // Changes based on mobile address bar size
      const viewportHeight = window.innerHeight;
      // Distance to bottom of the container
      const distToBottom = containerRef.current.getBoundingClientRect().top + containerRef.current.getBoundingClientRect().height;

      if (viewportHeight > distToBottom) {
        // if bottom is above the fold - use full height value to position at bottom
        setIndicatorPosition(containerRef.current.getBoundingClientRect().height)
      } else {
        // else bottom is below the fold - use visible height + size scrolled out of view
        setIndicatorPosition(window?.innerHeight + Math.abs(containerRef.current.getBoundingClientRect().top));
      }
    };

    onResize();

    window.addEventListener('scroll', onResize);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const c: Styles<typeof styles> = useStyles({ ...props, indicatorPosition });

  const hasMultipleSlides = children?.length > 1;

  const controls = hasMultipleSlides && (
    <div>
      <Icon
        name="ff-icon-keyboard_arrow_left"
        className={cx(c.controlLeft, c.control)}
        onClick={() => sliderRef.current.slickPrev()}
      />
      <Icon
        name="ff-icon-keyboard_arrow_right"
        className={cx(c.controlRight, c.control)}
        onClick={() => sliderRef.current.slickNext()}
      />
    </div>
  );

  const autoplaySpeed = !isNaN(carouselAutoplaySpeed) ?
    parseInt(parseFloat(carouselAutoplaySpeed) * 1000) : 8000;

  const sliderSettings = {
    ...defaultSliderSettings,
    dots: hasMultipleSlides,
    dotsClass: cx('slick-dots', c.dot, c.bottomUITransition),
    autoplay: hasMultipleSlides,
    autoplaySpeed
  };

  return (
    <div ref={containerRef} className={c.container}>
      {controlsEnabled && controls}
      <ReactSlickSlider
        ref={sliderRef}
        {...sliderSettings}
        className={c.sliderOverflow}
      >
        { children }
      </ReactSlickSlider>
      <Icon name="ff-icon-keyboard_arrow_down" className={cx(c.scrollIndicator, c.bottomUITransition)} />
    </div>
  );
}

const styles = {
  container: ({ indicatorPosition }) => ({
    scrollSnapAlign: 'start',
    zIndex: zIndices.homePageCarousel, // zIndex to let HomepageCarousel go above ProductSlideShow
    width: '100%',
    // use doc-height from product slide show if exists, to match the height of product slide show
    height: `var(--doc-height, max(100vh, ${SLIDE_MIN_HEIGHT}px))`,
    position: 'relative',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    '&:before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: 'auto',
      top: '0',
      left: '0',
      pointerEvents: 'none'
    },
    '& .slick-list': {
      height: '100%'
    },
    '& .slick-dots': {
      '@media(max-width:576px)': {
        top: indicatorPosition ? `${indicatorPosition - 44}px` : undefined,
        bottom: !indicatorPosition ? '44px' : undefined,
      },
      '@media(max-width:1024px)': {
        top: indicatorPosition ? `${indicatorPosition - 68}px` : undefined,
        bottom: !indicatorPosition ? '68px' : undefined,
      },
      '@media(min-width:1025px)': {
        top: indicatorPosition ? `${indicatorPosition - 80}px` : undefined,
        bottom: !indicatorPosition ? '80px' : undefined,
      },
    },
    '& .slick-dots li': {
      margin: 'auto'
    },
    '& .slick-dots li button:before': {
      fontSize: '12x',
      transition: 'all .2s ease',
      color: 'white'
    },
    '& .slick-dots li.slick-active button:before': {
      fontSize: '12x',
      color: 'white'
    },
    '& .slick-track:nth-child(2) > div ': {
      backgroundColor: 'blue',
    },
    // Zoom-in animation for first visible slide (second item)
    '& .slick-slide:nth-child(2) img ': {
      animation: '$zoomIn 1.5s ease-in-out',
    }
  }),
  bottomUITransition: {
    transition: 'top .75s ease-out 0.25s, bottom .75s ease-out 0.25s',
    animationDelay: '1.5s',
    animationTimingFunction: 'ease-in-out',
  },
  dot: {
    left: '0px',
    animationName: '$fadeIn',
    '& .slick-active': {
      '& button': {
        '&::before': {
          color: 'white !important',
          fontSize: '12px !important'
        }
      }
    },
    '& button': {
      '&::before': {
        color: 'white !important',
        fontSize: '12px !important'
      }
    },
  },
  control: {
    height: 'auto',
    top: '50%',
    width: '40px',
    fontSize: '40px',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    color: 'white',
    pointerEvents: 'auto',
    zIndex: '2',
    '-webkit-tap-highlight-color': 'transparent',
    '@media(max-width: 570px)': {
      width: '10px',
      fontSize: '36px'
    },
    animation: '$fadeIn 1.5s ease-in-out',
  },
  controlLeft: {
    left: '40px',
    '@media(max-width: 570px)': {
      left: '14px'
    },
  },
  controlRight: {
    right: '40px',
    '@media(max-width: 570px)': {
      right: '14px'
    },
  },
  sliderOverflow: {
    overflow: 'hidden'
  },
  scrollIndicator: {
    bottom: '5px',
    color: 'white',
    fontSize: '36px',
    position: 'absolute',
    textAlign: 'center',
    opacity: '0.5',
    width: '100%',
    animationName: '$indicatorFadeIn',
  },
  '@keyframes zoomIn': {
    from: {
      transform: 'scale3d(1.25, 1.25, 1)'
    },
    to: {
      transform: 'scale3d(1, 1, 1)'
    }
  },
  '@keyframes fadeIn': {
    from: {
      opacity: '0'
    },
    to: {
      opacity: '1'
    }
  },
  '@keyframes indicatorFadeIn': {
    from: {
      opacity: '0'
    },
    to: {
      opacity: '0.5'
    }
  }
};

const useStyles = createUseStyles(styles);
